import { useTranslation } from 'react-i18next';
import type { GoalProgress as GP } from '@flugwettrennen/shared';

/**
 * Fortschritt aller Ziele einer Runde. Bei mehreren Zielen steht über jedem
 * Block dessen Name; mit einem Ziel sieht die Anzeige aus wie früher.
 */
export function GoalProgress({ progress }: { progress: GP[] }) {
  const { t } = useTranslation();
  const titled = progress.length > 1;
  return (
    <div className="stack" style={{ gap: 14 }}>
      {progress.map((p) => (
        <div key={p.type} data-testid="goal-progress" className="stack" style={{ gap: 6 }}>
          {titled && <div className="eyebrow">{t(`goals.${p.type}`)}</div>}
          <GoalProgressBlock progress={p} />
        </div>
      ))}
    </div>
  );
}

function GoalProgressBlock({ progress }: { progress: GP }) {
  const { t } = useTranslation();
  const deg = Math.round(Math.abs(progress.longitudeTraversed ?? 0));
  const checklist = progress.checklist ?? [];
  const done = checklist.filter((c) => c.done).length;

  let summary = '';
  switch (progress.type) {
    case 'world_east':
    case 'world_west':
      summary = t('goals.summary.goal.world.summary', { deg });
      break;
    case 'europe_countries':
      summary = t('goals.summary.goal.europe.summary', { done, total: checklist.length });
      break;
    case 'top_islands':
      summary = t('goals.summary.goal.islands.summary', { done, total: checklist.length });
      break;
    case 'top_airlines':
      summary = t('goals.summary.goal.airlines.summary', { done, total: checklist.length });
      break;
    case 'antipode':
      summary = t('goals.summary.goal.antipode.summary', { target: progress.antipodeTargetIata ?? '?' });
      break;
    case 'antipode_world':
      summary = t('goals.summary.goal.antipodeWorld.summary', {
        target: progress.antipodeTargetIata ?? '?',
        deg,
      });
      break;
    default:
      summary = t('goals.summary.goal.free.summary');
  }

  const showBar = progress.type === 'world_east' || progress.type === 'world_west' || progress.type === 'antipode_world';
  const barPct = Math.min(100, (deg / 360) * 100);

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="mono" style={{ fontSize: '0.9rem' }}>{summary}</div>
      {showBar && (
        <div className="progress-bar" aria-label={summary}>
          <span style={{ width: `${barPct}%` }} />
        </div>
      )}
      {checklist.length > 0 && (
        <div className="checklist">
          {checklist.map((c) => (
            <div key={c.id} className={`check-item ${c.done ? 'done' : ''}`}>
              <span className="box">{c.done ? '✓' : ''}</span>
              <span>{c.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
